'use client';

import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { cn } from "../../utils/cn";

/**
 * Tooltip — Radix Tooltip wrapper.
 *
 * Sides: top | bottom | left | right | start | end (start/end swap under RTL).
 * `delayDuration` controls hover delay (default 300ms).
 * `showArrow` toggles the arrow indicator.
 *
 * Composition:
 *   <TooltipProvider>
 *     <Tooltip>
 *       <TooltipTrigger asChild><Button>Hover</Button></TooltipTrigger>
 *       <TooltipContent side="top">Body</TooltipContent>
 *     </Tooltip>
 *   </TooltipProvider>
 */

export const TooltipProvider = TooltipPrimitive.Provider;
export const Tooltip = TooltipPrimitive.Root;
export const TooltipTrigger = TooltipPrimitive.Trigger;
export const TooltipPortal = TooltipPrimitive.Portal;

type LogicalSide = "top" | "bottom" | "left" | "right" | "start" | "end";

function useIsRtl(): boolean {
  const [rtl, setRtl] = React.useState(false);
  React.useEffect(() => {
    if (typeof document === "undefined") return;
    setRtl(document.documentElement.getAttribute("dir") === "rtl");
  }, []);
  return rtl;
}

function resolveSide(
  side: LogicalSide,
  rtl: boolean,
): "top" | "bottom" | "left" | "right" {
  if (side === "start") return rtl ? "right" : "left";
  if (side === "end") return rtl ? "left" : "right";
  return side;
}

export interface TooltipContentProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>,
    "side"
  > {
  /** Logical or physical side. `start`/`end` swap in RTL. */
  side?: LogicalSide;
  /** Render the arrow indicator. Default: true. */
  showArrow?: boolean;
}

export const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  TooltipContentProps
>(function TooltipContent(
  { className, children, side = "top", sideOffset = 6, showArrow = true, ...props },
  ref,
) {
  const rtl = useIsRtl();
  const resolved = resolveSide(side, rtl);
  return (
    <TooltipPortal>
      <TooltipPrimitive.Content
        ref={ref}
        data-slot="tooltip-content"
        data-logical-side={side}
        side={resolved}
        sideOffset={sideOffset}
        className={cn(
          "z-50 max-w-[240px] px-space-8 py-space-4",
          "bg-ink-primary text-chrome-surface",
          "rounded-chip text-caption-12 shadow-md",
          "data-[state=delayed-open]:animate-in data-[state=closed]:animate-out",
          "data-[state=closed]:fade-out-0 data-[state=delayed-open]:fade-in-0",
          className,
        )}
        {...props}
      >
        {children}
        {showArrow && (
          <TooltipPrimitive.Arrow className="fill-ink-primary" />
        )}
      </TooltipPrimitive.Content>
    </TooltipPortal>
  );
});
TooltipContent.displayName = "TooltipContent";
